import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from './AdminLayout';
import AdminPanel from './AdminPanel';
import BrowserEmail from './BrowserEmail';
import EmailMigration from './EmailMigration';
import MigrationServices from './MigrationServices';
import NewsletterDashboard from './NewsletterDashboard';
import { EmailConfiguration } from './EmailConfiguration';
import { CampaignManagement } from './CampaignManagement';
import CampaignsList from './CampaignsList';
import UnderConstruction from './UnderConstruction';
import { config } from '../config';

// Type for AdminPanel props
type AdminPanelComponent = React.ComponentType<{ companyIdProp?: string }>;

const UnifiedDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState('admin');

  // Initialize token and companyId from localStorage immediately
  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem('dashzap_token');
  });

  const [companyId, setCompanyId] = useState<string | null>(() => {
    const dashzapToken = localStorage.getItem('dashzap_token');
    if (dashzapToken) {
      try {
        const payload = JSON.parse(atob(dashzapToken.split('.')[1]));
        return payload.companyId;
      } catch {
        return null;
      }
    }
    return null;
  });

  useEffect(() => {
    const dashzapToken = localStorage.getItem('dashzap_token');

    console.log('🔍 UnifiedDashboard: Checking auth token...');
    console.log('Token exists:', !!dashzapToken);

    if (!dashzapToken) {
      console.log('❌ No token found, redirecting to home');
      navigate('/');
      return;
    }

    try {
      // Decode JWT to get company ID
      const payload = JSON.parse(atob(dashzapToken.split('.')[1]));
      const extractedCompanyId = payload.companyId;

      console.log('✅ Token decoded successfully');
      console.log('Company ID:', extractedCompanyId);

      // If still in onboarding, redirect back
      if (extractedCompanyId === 'onboarding') {
        console.log('⚠️ Still in onboarding, redirecting...');
        navigate('/onboarding');
        return;
      }

      console.log('✅ Token and companyId already set in initial state');
    } catch (error) {
      console.error('❌ Error decoding token:', error);
      navigate('/');
    }
  }, [navigate]);

  // Debug log when token/companyId changes
  useEffect(() => {
    console.log('🔄 State changed - Token:', !!token, 'CompanyId:', !!companyId);
  }, [token, companyId]);

  const handleLogout = () => {
    localStorage.removeItem('dashzap_token');
    navigate('/');
  };

  const handleViewChange = (view: string) => {
    setCurrentView(view);
  };

  // Don't render until we have token and companyId
  if (!token || !companyId) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
          <p className="mt-2 text-sm text-gray-500">Token: {token ? '✓' : '✗'} | Company: {companyId ? '✓' : '✗'}</p>
          <button
            onClick={() => {
              console.log('Debug info:');
              console.log('- Token:', token);
              console.log('- CompanyId:', companyId);
              console.log('- LocalStorage token:', localStorage.getItem('dashzap_token'));
            }}
            className="mt-4 text-sm text-indigo-600 underline"
          >
            Debug Info (check console)
          </button>
        </div>
      </div>
    );
  }

  // Render content based on current view
  const renderContent = () => {
    console.log('📄 Rendering view:', currentView);

    // Don't render until we have companyId
    if (!companyId) {
      return <div>Loading...</div>;
    }

    const AdminPanelTyped = AdminPanel as AdminPanelComponent;

    switch (currentView) {
      case 'admin':
      case 'company':
        return <AdminPanelTyped companyIdProp={companyId} />;

      case 'browser-email':
        return (
          <BrowserEmail
            companyId={companyId}
            apiBaseUrl={config.API_BASE_URL}
            token={token}
          />
        );

      case 'form-analytics':
        return <UnderConstruction title="Form Analytics" message="Email form analytics and insights dashboard is coming soon." />;

      case 'migration':
        return <UnderConstruction title="Migration Services" message="Email migration consultation and services coming soon." />;

      case 'email-backup':
        return <EmailMigration />;

      case 'newsletter-dashboard':
        return <NewsletterDashboard />;

      case 'email-config':
        return <EmailConfiguration />;

      case 'create-campaign':
        return <CampaignManagement />;

      case 'campaigns-list':
        return <CampaignsList />;

      default:
        return <AdminPanelTyped companyIdProp={companyId} />;
    }
  };

  console.log('🎨 About to render AdminLayout with:', { currentView, hasToken: !!token, hasCompanyId: !!companyId });

  return (
    <AdminLayout
      currentView={currentView}
      onViewChange={handleViewChange}
      onLogout={handleLogout}
    >
      {renderContent()}
    </AdminLayout>
  );
};

export default UnifiedDashboard;
